<template>
  <Container>
    <RevCard
      class="flex w-full flex-col gap-32 p-16 md:flex-row md:gap-56 md:px-32 md:py-24"
    >
      <ProductSummary
        :averageRate
        :backboxGrade
        class="shrink-0"
        :hasEdit="true"
        :product
        @edit="handleEdit"
      />

      <div class="flex w-full flex-col">
        <h3 v-if="titleTexts.title" class="heading-3">
          {{ titleTexts.title }}
        </h3>
        <p v-if="titleTexts.subtitle" class="text-static-default-low body-1">
          {{ titleTexts.subtitle }}
        </p>
        <CareBlock
          v-if="averageRate && averageRate <= NEGATIVE_REVIEW_MAX_LIMIT"
          class="mt-24"
        />
        <AppIncentiveBlock
          v-if="averageRate && averageRate > NEGATIVE_REVIEW_MAX_LIMIT"
          class="mt-24"
        />
      </div>
    </RevCard>
  </Container>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import type { Grade } from '@backmarket/http-api/src/api-models/Grade'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { RevCard } from '@ds/components/Card'

import type { Product } from '~/api/reviews/types/GetReviewCollectionResponse'

import { NEGATIVE_REVIEW_MAX_LIMIT } from '../../ReviewCollection.constants'
import { useReviewCollectionTracking } from '../../composables/useReviewCollectionTracking'
import AppIncentiveBlock from '../AppIncentiveBlock/AppIncentiveBlock.vue'
import CareBlock from '../Care/CareBlock.vue'
import Container from '../Container/Container.vue'
import ProductSummary from '../ProductSummary/ProductSummary.vue'

import translations from './ReviewSummary.translations'

const i18n = useI18n()
const { trackEditReview } = useReviewCollectionTracking()

const {
  product,
  backboxGrade = undefined,
  averageRate = undefined,
  goToFirstStep,
} = defineProps<{
  product: Product
  backboxGrade?: Grade
  averageRate?: number
  goToFirstStep: () => void
}>()

const isPositive = computed(() => {
  return (averageRate && averageRate > NEGATIVE_REVIEW_MAX_LIMIT) || false
})

const titleTexts = computed(() => {
  if (isPositive.value) {
    return {
      title: i18n(translations.positiveTitle),
      subtitle: i18n(translations.positiveSubtitle),
    }
  }

  return {
    title: i18n(translations.negativeTitle),
    subtitle: i18n(translations.negativeSubtitle),
  }
})

function handleEdit() {
  trackEditReview({ isPositive: isPositive.value })
  goToFirstStep()
}
</script>
